/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, TextField, InputAdornment, Button, Typography,
  Table, TableBody, TableCell, TableRow, Alert, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TimelineIcon from '@mui/icons-material/Timeline';
import TimelineDialog from '../components/TimelineDialog';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import { api } from '../../../api';
import { usePharmacy } from '../../../contexts/PharmacyContext';
import { useListUrlState } from '../../../hooks/useListUrlState';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';
import printCreditNote from './printCreditNote';

const TEAL = '#00798E';
const TEAL_DARK = '#005f70';
const TEAL_LIGHT = '#e0f4f7';

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS = {
  OPEN:    { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', label: 'Open',    dot: '#43a047' },
  USED:    { bg: '#ede7f6', color: '#5e35b1', border: '#ce93d8', label: 'Used',    dot: '#7e57c2' },
  EXPIRED: { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0', label: 'Expired', dot: '#bdbdbd' },
};

// ── Stat Card ──────────────────────────────────────────────────

function StatCard({ icon, label, value, accent, sub }) {
  return (
    <Box sx={{
      flex: '1 1 160px', minWidth: 140, borderRadius: 3,
      background: '#fff',
      border: `1.5px solid ${accent}33`,
      boxShadow: `0 2px 12px ${accent}18`,
      px: 2.5, py: 2,
      display: 'flex', alignItems: 'center', gap: 1.8,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: `0 6px 24px ${accent}28` },
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2.5,
        background: `linear-gradient(135deg, ${accent}22, ${accent}44)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {React.cloneElement(icon, { sx: { color: accent, fontSize: 22 } })}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1a202c', lineHeight: 1.4, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.65rem', color: '#bdbdbd', mt: 0.2 }}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

// ── Status Badge ───────────────────────────────────────────────

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.EXPIRED;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.6,
      px: 1.2, py: 0.35,
      borderRadius: '20px',
      background: s.bg,
      border: `1px solid ${s.border}`,
    }}>
      <Box sx={{
        width: 6, height: 6, borderRadius: '50%',
        background: s.dot,
        ...(status === 'OPEN' && {
          boxShadow: `0 0 0 2px ${s.dot}44`,
          animation: 'pulse 2s infinite',
        }),
      }} />
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.label}</Typography>
    </Box>
  );
}

// ── Settle Dialog ──────────────────────────────────────────────

function SettleDialog({ open, cn, onClose, onConfirm }) {
  const [method, setMethod] = useState('CASH');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) { setMethod('CASH'); setNotes(''); setErr(''); }
  }, [open]);

  const handleConfirm = async () => {
    setLoading(true); setErr('');
    try {
      await onConfirm({ settle_method: method, notes: notes.trim() || undefined });
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Settlement failed');
    } finally { setLoading(false); }
  };

  if (!cn) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, px: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalAtmIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Settle Credit Note</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, px: 2.5 }} onKeyDown={formKeyHandler(handleConfirm)}>
        <Box sx={{ mb: 2.5, p: 2, borderRadius: 2.5, background: `linear-gradient(135deg, ${TEAL}0a, ${TEAL}18)`, border: `1px solid ${TEAL}33` }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: TEAL, fontFamily: 'monospace' }}>{cn.credit_note_no}</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#4a5568', mt: 0.3 }}>{cn.original_bill?.patient_name || '—'}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>Available Balance:</Typography>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#2e7d32', fontFamily: 'monospace' }}>{fmt(cn.balance_amount)}</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9e9e9e', mb: 1.2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Settlement Method</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
          {[{ key: 'CASH', label: '💵 Cash Refund' }, { key: 'NEXT_BILL', label: '📋 Adjust in Next Bill' }].map(opt => (
            <Button key={opt.key} size="small"
              onClick={() => setMethod(opt.key)}
              variant={method === opt.key ? 'contained' : 'outlined'}
              sx={{
                fontSize: '0.73rem', borderRadius: 2.5, fontWeight: 600, flex: 1, py: 1,
                ...(method === opt.key
                  ? { background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`, color: '#fff !important', boxShadow: `0 4px 12px ${TEAL}44`, '&:hover': { background: TEAL_DARK, color: '#fff !important', boxShadow: `0 4px 12px ${TEAL}55` } }
                  : { color: '#4a5568', borderColor: '#d1d5db', '&:hover': { borderColor: TEAL, color: TEAL, background: TEAL_LIGHT } }),
              }}>
              {opt.label}
            </Button>
          ))}
        </Box>
        <TextField fullWidth size="small" label="Notes (optional)" value={notes}
          onChange={e => setNotes(e.target.value)} multiline rows={2}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
        {err && <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.78rem', py: 0.5, borderRadius: 2 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ fontSize: '0.78rem', color: '#9e9e9e', fontWeight: 600, '&:hover': { color: '#9e9e9e', background: 'rgba(0,0,0,0.04)' } }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={loading}
          sx={{ background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`, color: '#fff !important', '&:hover': { background: TEAL_DARK, color: '#fff !important' }, fontSize: '0.78rem', fontWeight: 700, px: 3, borderRadius: 2.5, boxShadow: `0 4px 12px ${TEAL}44` }}>
          {loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Confirm Settlement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────

export default function CreditNotesPage() {
  const navigate = useNavigate();
  const { selectedPharmacy } = usePharmacy();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState((urlParams && parseInt(urlParams.get('page'), 10)) || 1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(urlParams?.get('status') || '');
  const [settleTarget, setSettleTarget] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const kb = useListKeyboard({
    rowCount: rows.length,
    enabled: !settleTarget && !showTimeline,
    onOpen: (i) => { const b = rows[i]?.original_bill; if (b?.id) navigate(`/pharmacy/billing/${b.id}`); },
  });
  const [timelineMeta, setTimelineMeta] = useState({ title: '', subtitle: '' });
  const searchTimer = useRef(null);
  const PER_PAGE = 20;

  const load = useCallback(async (q, status, pg) => {
    setLoading(true); setError('');
    try {
      writeParams({ search: q, status, page: pg });
      const params = { page: pg, per_page: PER_PAGE };
      if (q) params.search = q;
      if (status) params.status = status;
      const res = await api.pharmacy.listAllCreditNotes(params);
      setRows(res.rows || []);
      setTotal(res.count || 0);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load credit notes');
    } finally { setLoading(false); }
  }, [writeParams]);

  useEffect(() => { load(search, statusFilter, page); }, [page]); // eslint-disable-line

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(val, statusFilter, 1); }, 400);
  };

  const handleStatusFilter = (s) => {
    const next = s === statusFilter ? '' : s;
    setStatusFilter(next); setPage(1); load(search, next, 1);
  };

  const openTimeline = async (cn) => {
    const billId = cn.irm_pharm_bills_id;
    const bill = cn.original_bill || {};
    setTimelineMeta({ title: cn.credit_note_no, subtitle: `${bill.bill_no || ''} · ${bill.patient_name || ''}` });
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await api.pharmacy.getBillingTimeline(billId);
      setTimelineEvents(Array.isArray(res) ? res : (res?.data || []));
    } catch { setTimelineEvents([]); }
    finally { setTimelineLoading(false); }
  };

  const handleSettle = async (dto) => {
    await api.pharmacy.settleCreditNote(settleTarget.id, dto);
    setSettleTarget(null);
    setSuccess('Credit note settled successfully');
    load(search, statusFilter, page);
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  // Compute quick stats from current rows
  const openCount = rows.filter(r => r.status === 'OPEN').length;
  const openBalance = rows.filter(r => r.status === 'OPEN').reduce((s, r) => s + Number(r.balance_amount || 0), 0);
  const usedCount = rows.filter(r => r.status === 'USED').length;

  const COL_HEADERS = ['CN No', 'Date', 'Patient', 'Mobile', 'Ref Bill', 'CN Amount', 'Balance', 'Status', 'Actions'];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(67,160,71,0.5); }
          50% { box-shadow: 0 0 0 4px rgba(67,160,71,0); }
        }
      `}</style>

      <MDBox py={3} px={0}>
        {/* ── Hero Header ── */}
        <Box sx={{
          borderRadius: 4,
          background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 60%, #003d4d 100%)`,
          px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
          boxShadow: `0 8px 32px ${TEAL}44`,
          position: 'relative', overflow: 'hidden',
        }}>
          {/* decorative circles */}
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -20, right: 80, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
            <IconButton onClick={() => navigate('/pharmacy/billing')}
              sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.28)' }, borderRadius: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{
              width: 46, height: 46, borderRadius: 2.5,
              background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ReceiptLongIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Pharmacy Billing</Typography>
              <Typography sx={{ color: '#fff', fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.1 }}>Credit Notes</Typography>
            </Box>
            <Box sx={{ ml: 1, px: 1.4, py: 0.4, borderRadius: 10, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>{total} total</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, zIndex: 1 }}>
            <Button
              onClick={() => navigate('/pharmacy/billing/returns')}
              sx={{
                background: 'rgba(255,255,255,0.15)', color: '#fff !important', fontWeight: 700,
                fontSize: '0.78rem', borderRadius: 2.5, px: 2.5, py: 1,
                border: '1.5px solid rgba(255,255,255,0.3)',
                backdropFilter: 'blur(6px)',
                '&:hover': { background: 'rgba(255,255,255,0.28)' },
              }}>
              Returns
            </Button>
            <Button
              onClick={() => navigate('/pharmacy/billing/payments')}
              sx={{
                background: 'rgba(255,255,255,0.15)', color: '#fff !important', fontWeight: 700,
                fontSize: '0.78rem', borderRadius: 2.5, px: 2.5, py: 1,
                border: '1.5px solid rgba(255,255,255,0.3)',
                backdropFilter: 'blur(6px)',
                '&:hover': { background: 'rgba(255,255,255,0.28)' },
              }}>
              View Payments
            </Button>
          </Box>
        </Box>

        {/* ── Stat Cards ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatCard icon={<AccountBalanceWalletIcon />} label="Open Balance" value={fmt(openBalance)} accent="#2e7d32" sub={`${openCount} open notes`} />
          <StatCard icon={<CheckCircleIcon />} label="Settled (this page)" value={usedCount} accent="#5e35b1" sub="credit notes used" />
          <StatCard icon={<HourglassEmptyIcon />} label="Total (this page)" value={rows.length} accent={TEAL} sub={`of ${total} total`} />
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.82rem' }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.82rem' }}>{success}</Alert>}

        {/* ── Main Card ── */}
        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>

          {/* Filter bar */}
          <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f4f8', background: '#fafcff' }}>
            <TextField
              {...kb.searchProps}
              size="small"
              placeholder="Search patient name, mobile or bill no…  ( / to focus )"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#b0bec5', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1, minWidth: 240,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3, fontSize: '0.82rem', background: '#fff',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: TEAL },
                  '&.Mui-focused fieldset': { borderColor: TEAL },
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#b0bec5', fontWeight: 600, mr: 0.5 }}>Filter:</Typography>
              {Object.entries(STATUS).map(([key, s]) => (
                <Chip
                  key={key}
                  label={s.label}
                  size="small"
                  clickable
                  onClick={() => handleStatusFilter(key)}
                  sx={{
                    fontSize: '0.7rem', fontWeight: 700, height: 26, borderRadius: '20px',
                    transition: 'all 0.18s',
                    background: statusFilter === key ? s.bg : '#f0f4f8',
                    color: statusFilter === key ? s.color : '#90a4ae',
                    border: `1.5px solid ${statusFilter === key ? s.border : '#e2e8f0'}`,
                    '&:hover': { background: s.bg, color: s.color, borderColor: s.border },
                  }}
                />
              ))}
              {statusFilter && (
                <Chip
                  label="Clear"
                  size="small"
                  clickable
                  onClick={() => handleStatusFilter(statusFilter)}
                  sx={{ fontSize: '0.65rem', height: 22, color: '#ef5350', background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '20px' }}
                />
              )}
            </Box>
          </Box>

          {/* Table */}
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <CircularProgress sx={{ color: TEAL }} size={36} />
              <Typography sx={{ fontSize: '0.8rem', color: '#b0bec5' }}>Loading credit notes…</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableBody>
                  <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                    {COL_HEADERS.map(h => (
                      <TableCell key={h} sx={{
                        fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)',
                        textTransform: 'uppercase', letterSpacing: 0.7,
                        py: 1.4, whiteSpace: 'nowrap',
                        borderBottom: 'none',
                        '&:first-of-type': { pl: 2.5 },
                        '&:last-of-type': { pr: 2.5 },
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <ReceiptLongIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>No credit notes found</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#d0d0d0', mt: 0.5 }}>Try adjusting your search or filter</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : rows.map((cn, i) => {
                    const bill = cn.original_bill || {};
                    return (
                      <TableRow key={cn.id} {...kb.getRowProps(i)} sx={{
                        background: i % 2 === 0 ? '#fff' : '#fafcff',
                        borderLeft: cn.status === 'OPEN' ? `3px solid ${STATUS.OPEN.dot}` : '3px solid transparent',
                        transition: 'background 0.15s, box-shadow 0.15s',
                        cursor: 'pointer',
                        '&:hover': {
                          background: TEAL_LIGHT,
                          boxShadow: `inset 0 0 0 1px ${TEAL}22`,
                        },
                        ...(kb.activeIdx === i ? ACTIVE_ROW_SX : {}),
                      }}>
                        {/* CN No */}
                        <TableCell sx={{ py: 1.5, pl: 2.5 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.2, py: 0.4, borderRadius: 1.5, background: '#f3e5f5', border: '1px solid #e1bee7' }}>
                            <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: '#6a1b9a', fontFamily: 'monospace', letterSpacing: 0.3 }}>
                              {cn.credit_note_no}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Date */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.76rem', color: '#546e7a', whiteSpace: 'nowrap' }}>{fmtDate(cn.credit_note_date)}</Typography>
                        </TableCell>

                        {/* Patient */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2d3748' }}>{bill.patient_name || '—'}</Typography>
                        </TableCell>

                        {/* Mobile */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#78909c' }}>{bill.patient_mobile || '—'}</Typography>
                        </TableCell>

                        {/* Ref Bill */}
                        <TableCell sx={{ py: 1.5 }}>
                          {bill.bill_no ? (
                            <Button
                              size="small"
                              endIcon={<OpenInNewIcon sx={{ fontSize: '11px !important' }} />}
                              onClick={() => navigate(`/pharmacy/billing/${bill.id}`)}
                              sx={{
                                fontSize: '0.73rem', color: TEAL, p: 0, minWidth: 0,
                                fontFamily: 'monospace', fontWeight: 700,
                                textDecoration: 'none',
                                '&:hover': { color: TEAL_DARK, background: 'transparent', textDecoration: 'underline' },
                              }}>
                              {bill.bill_no}
                            </Button>
                          ) : <Typography sx={{ fontSize: '0.75rem', color: '#ccc' }}>—</Typography>}
                        </TableCell>

                        {/* Amount */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 700, color: '#2d3748', whiteSpace: 'nowrap' }}>
                            {fmt(cn.amount)}
                          </Typography>
                        </TableCell>

                        {/* Balance */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{
                            fontSize: '0.88rem', fontFamily: 'monospace', fontWeight: 800,
                            color: cn.status === 'OPEN' ? '#2e7d32' : '#b0bec5',
                            whiteSpace: 'nowrap',
                          }}>
                            {fmt(cn.balance_amount)}
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 1.5 }}>
                          <StatusBadge status={cn.status} />
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ py: 1.5, pr: 2.5, whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                            <Tooltip title="Print Credit Note" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  // The list row's original_bill is attribute-restricted (no
                                  // patient/couple/spouse code) — fetch the full enriched
                                  // record so the print carries the barcode strip.
                                  api.pharmacy.getCreditNote(cn.id)
                                    .then(full => printCreditNote(full || cn, selectedPharmacy))
                                    .catch(() => printCreditNote(cn, selectedPharmacy));
                                }}
                                sx={{
                                  color: TEAL,
                                  border: `1px solid ${TEAL}33`,
                                  borderRadius: 1.5,
                                  width: 30, height: 30,
                                  '&:hover': { background: TEAL_LIGHT, borderColor: TEAL },
                                }}>
                                <PrintIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            {cn.irm_pharm_bills_id && (
                              <Tooltip title="View Bill Journey" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => openTimeline(cn)}
                                  sx={{
                                    color: '#3949ab',
                                    border: '1px solid #3949ab33',
                                    borderRadius: 1.5,
                                    width: 30, height: 30,
                                    '&:hover': { background: '#e8eaf6', borderColor: '#3949ab' },
                                  }}>
                                  <TimelineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {cn.status === 'OPEN' && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => setSettleTarget(cn)}
                                sx={{
                                  fontSize: '0.68rem', fontWeight: 700, borderRadius: 2,
                                  py: 0.4, px: 1.5, minWidth: 0, height: 28,
                                  background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`,
                                  color: '#fff !important',
                                  boxShadow: `0 2px 8px ${TEAL}44`,
                                  '&:hover': { background: TEAL_DARK, color: '#fff !important', boxShadow: `0 4px 14px ${TEAL}55` },
                                }}>
                                Settle
                              </Button>
                            )}
                            {cn.status === 'USED' && (
                              <Tooltip title="Settled" arrow>
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: 3, py: 1.75,
              borderTop: '1px solid #f0f4f8',
              background: '#fafcff',
            }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae', fontWeight: 500 }}>
                Showing <b style={{ color: '#546e7a' }}>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b style={{ color: '#546e7a' }}>{total}</b> results
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  sx={{
                    border: '1.5px solid #e2e8f0', borderRadius: 2, width: 32, height: 32,
                    color: page <= 1 ? '#d0d0d0' : TEAL,
                    '&:hover:not(:disabled)': { background: TEAL_LIGHT, borderColor: TEAL },
                  }}>
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, idx) => {
                  const p = totalPages <= 5 ? idx + 1 : (page <= 3 ? idx + 1 : page - 2 + idx);
                  if (p < 1 || p > totalPages) return null;
                  return (
                    <Button
                      key={p}
                      size="small"
                      onClick={() => setPage(p)}
                      sx={{
                        minWidth: 32, height: 32, borderRadius: 2, fontSize: '0.78rem', fontWeight: 700,
                        border: `1.5px solid ${p === page ? TEAL : '#e2e8f0'}`,
                        background: p === page ? `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})` : 'transparent',
                        color: p === page ? '#fff !important' : '#78909c',
                        boxShadow: p === page ? `0 2px 8px ${TEAL}44` : 'none',
                        '&:hover': p === page ? { color: '#fff !important' } : { background: TEAL_LIGHT, borderColor: TEAL, color: TEAL },
                      }}>
                      {p}
                    </Button>
                  );
                })}
                <IconButton
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  sx={{
                    border: '1.5px solid #e2e8f0', borderRadius: 2, width: 32, height: 32,
                    color: page >= totalPages ? '#d0d0d0' : TEAL,
                    '&:hover:not(:disabled)': { background: TEAL_LIGHT, borderColor: TEAL },
                  }}>
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Card>
      </MDBox>

      <SettleDialog open={!!settleTarget} cn={settleTarget} onClose={() => setSettleTarget(null)} onConfirm={handleSettle} />

      <TimelineDialog
        open={showTimeline}
        onClose={() => setShowTimeline(false)}
        title={timelineMeta.title}
        subtitle={timelineMeta.subtitle}
        events={timelineEvents}
        loading={timelineLoading}
      />

      <Footer />
    </DashboardLayout>
  );
}
